import { Component, ElementRef, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import _ = require('lodash');
import { FilterCriteria } from '../../../models/filter-criteria';
import { StoreIdentifier } from '../../../models/store-identifier.model';
import { AuthorizationService } from '../../../services/authorization.service';
import { GlobalFilterService } from '../../../services/global-filter.service';
import { StoreIdentifierService } from '../../../services/remittance-entry/store-identifier.service';

export enum KEYCODES {
  TAB = 9,
  NUMPAD_PLUS = 107,
}

@Component({
  selector: 'app-store-identifier',
  templateUrl: './store-identifier.component.html',
  styleUrls: ['./store-identifier.component.css'],
})

//  This is a Smart component
export class StoreIdentifierComponent implements OnInit, OnChanges {
  @ViewChild('ncpdpText') ncpdpChild: ElementRef
  public storeSearchForm: FormGroup;

  //  Members
  formLoading: boolean
  clientId: string
  private filterCriteria = new FilterCriteria('Location_CorporateLocationNumber', 1, 10000, 0)
  private _totalItemCount = 0

  //#region Properties

  get totalItemCount(): number {
    return this._totalItemCount
  }

  get ncpdp(): FormControl {
    return this.storeSearchForm.get('ncpdp') as FormControl;
  }

  get storeId(): FormControl {
    return this.storeSearchForm.get('storeId') as FormControl
  }

  get npi(): FormControl {
    return this.storeSearchForm.get('npi') as FormControl
  }

  get isValid(): boolean {
    return ((this.ncpdp.value && this.ncpdp.valid) ||
      (this.storeId.value && this.storeId.valid) ||
      (this.npi.value && this.npi.valid));
  }
  //#endregion

  public storeIdentifiers: StoreIdentifier[] = [];

  constructor(
    private authorizationService: AuthorizationService,
    private globalFilterService: GlobalFilterService,
    private storeIdentifierService: StoreIdentifierService,
    private builder: FormBuilder) {
      if (this.globalFilterService.selectedEnvironment) {
        this.clientId = this.globalFilterService.selectedEnvironment.ClientId4Digit
      }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadData()
    this.createSubscriptions()
  }

  ngOnChanges(changes: SimpleChanges): void {
    let foo = ''
  }

  private createSubscriptions(): void {
  }

  private initForm() {
    this.storeSearchForm = this.builder.group({
      ncpdp: this.builder.control(null),
      storeId: this.builder.control(null),
      npi: this.builder.control(null),
    });
  }

  private async loadData() {
    this.formLoading = true;
    this.callNotifyService()

    await this.storeIdentifierService.loadStoreIdentifiers(this.clientId,
      this.filterCriteria)
      .then((stores) => {
        if (stores.TotalItemCount === 0) {
          toastr.error('No Stores were found.  If this problem continues please contact your account team.');
          return;
        }

        this.storeIdentifiers = stores.Items
        this._totalItemCount = stores.TotalItemCount
        this.formLoading = false
      })
      .catch(() => {
        toastr.error('An error occurred while loading Store information. If this problem continues please contact your account team.');
        this.formLoading = false
      })
  }

  checkForNavigationKey(event: KeyboardEvent) {
    switch (event.keyCode) {
      case KEYCODES.NUMPAD_PLUS:
      case KEYCODES.TAB:
        this.onLookup(event)
        break;

      default:
        break;
    }
  }

  onLookup(event: any): void {
    let key = event.target.value
    let matches = []

    //  Filter the set of values based the field we're in and check for matches.
    //  If the key is null, mark the field as pristine for CSS purposes. Otherwise,
    //  if we have a single match, update the other fields or mark this as an error
    //  otherwise.
    switch (event.target.name) {
      case 'ncpdp':
        if (!key) {
          this.ncpdp.markAsPristine()
          return
        } else {
          this.ncpdp.markAsDirty()
        }
        matches = _.filter(this.storeIdentifiers, (ident) => ident.Location_CorporateLocationNumber === key)

        if (matches.length === 1) {
          this.storeId.setValue(matches[0].StoreId)
          this.storeId.markAsDirty()
          this.npi.setValue(matches[0].Location_NationalProviderIdentifier)
          this.npi.markAsDirty()
          this.ncpdp.setErrors(null)

          // This is being added to debug a production issue re: SDHEAL-4255
          console.log('Found StoreIdentifier By NCPDP.  Calling notify');
          console.log(matches[0]);
          console.log(new Date().getTime());

          this.storeIdentifierService.notifyFormComplete(matches[0])
        } else {
          this.ncpdp.setErrors({ invalid: true })
        }
        break;

      case 'storeId':
        if (!key) {
          this.storeId.markAsPristine()
          return
        } else {
          this.storeId.markAsDirty()
        }
        matches = _.filter(this.storeIdentifiers, (ident) => ident.StoreId === key)

        if (matches.length === 1) {
          this.ncpdp.setValue(matches[0].Location_CorporateLocationNumber)
          this.ncpdp.markAsDirty()
          this.npi.setValue(matches[0].Location_NationalProviderIdentifier)
          this.npi.markAsDirty()
          this.storeId.setErrors(null)

          // This is being added to debug a production issue re: SDHEAL-4255
          console.log('Found StoreIdentifier By StoreID.  Calling notify');
          console.log(matches[0]);
          console.log(new Date().getTime());

          this.storeIdentifierService.notifyFormComplete(matches[0])
        } else {
          this.storeId.setErrors({ invalid: true })
        }
        break;

      case 'npi':
        if (!key) {
          this.npi.markAsPristine()
          return
        } else {
          this.npi.markAsDirty()
        }
        matches = _.filter(this.storeIdentifiers, (ident) => ident.Location_NationalProviderIdentifier === key)

        if (matches.length === 1) {
          this.ncpdp.setValue(matches[0].Location_CorporateLocationNumber)
          this.ncpdp.markAsDirty()
          this.storeId.setValue(matches[0].StoreId)
          this.storeId.markAsDirty()
          this.npi.setErrors(null)

          // This is being added to debug a production issue re: SDHEAL-4255
          console.log('Found StoreIdentifier By NPI.  Calling notify');
          console.log(matches[0]);
          console.log(new Date().getTime());

          this.storeIdentifierService.notifyFormComplete(matches[0])
        } else {
          this.npi.setErrors({ invalid: true })
        }
        break;
    }
  }

  resetForm(): void {
    this.storeSearchForm.patchValue({
      ncpdp: '', storeId: '', npi: '',
    })
  }

  private callNotifyService() {
    if (!this.authorizationService.HealthcareTopBar || !this.authorizationService.HealthcareTopBar.notifyActivity) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/remittance-entry/store-identifier/store-identifier.component.ts